import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

const Skills = () => {
  const { technicalSkills, softSkills } = useSelector(state => state.skills);
  const [activeCategory, setActiveCategory] = useState(null);
  const [skillsView, setSkillsView] = useState('technical');
  
  useEffect(() => {
    document.title = "Skills | Musa Yusuf Magoban";
    window.scrollTo(0, 0);
  }, []);

  const getSkillCategoryIcon = (category) => {
    const iconMap = {
      'SOC Tools': 'Shield',
      'Malware Analysis': 'Bug',
      'Programming': 'Code',
      'IT Support': 'Monitor',
      'Regulatory Awareness': 'Award',
      'Digital Forensics & Threat Detection': 'Search'
    };
    return iconMap[category] || 'Code';
  };

  const skillStats = [
    { number: technicalSkills.length, label: "Technical Categories", icon: "Code", color: "primary" },
    { number: technicalSkills.reduce((acc, cat) => acc + cat.skills.length, 0), label: "Technical Skills", icon: "Zap", color: "secondary" },
    { number: softSkills.length, label: "Soft Skills", icon: "User", color: "accent" },
    { number: "2+", label: "Years Experience", icon: "Clock", color: "primary" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Skills | Musa Yusuf Magoban</title>
        <meta name="description" content="Technical and soft skills in cybersecurity, SOC operations, malware analysis, and security automation." />
      </Helmet>

      <Header />

      <main className="pt-16">
        {/* Enhanced Header Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/5 relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-2 bg-secondary/10 rounded-full text-secondary font-medium text-sm mb-6">
                <Icon name="Brain" size={16} className="mr-2" />
                Expertise & Capabilities
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
                Technical & <span className="text-primary">Soft Skills</span>
              </h1>
              <p className="text-xl text-text-secondary max-w-4xl mx-auto leading-relaxed">
                A comprehensive skill set developed through <span className="text-primary font-medium">hands-on projects</span>, 
                <span className="text-secondary font-medium"> professional certifications</span>, and 
                <span className="text-accent font-medium"> real-world cybersecurity experience</span>.
              </p>
              
              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 max-w-4xl mx-auto">
                {skillStats.map((stat, index) => (
                  <div key={index} className="bg-surface/80 backdrop-blur-sm rounded-xl p-4 security-shadow hover:security-shadow-strong transition-all duration-300 transform hover:-translate-y-1">
                    <div className={`w-10 h-10 bg-${stat.color}/10 rounded-lg flex items-center justify-center mx-auto mb-2`}>
                      <Icon name={stat.icon} size={20} className={`text-${stat.color}`} />
                    </div>
                    <div className="text-2xl font-bold text-foreground">{stat.number}</div>
                    <div className="text-sm text-text-secondary">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Skills Toggle */}
        <section className="py-8 bg-surface/50 border-b border-border/50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center">
              <div className="inline-flex bg-muted rounded-lg p-1">
                <button
                  onClick={() => setSkillsView('technical')}
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    skillsView === 'technical'
                      ? 'bg-primary text-primary-foreground shadow-lg'
                      : 'text-text-secondary hover:text-foreground'
                  }`}
                >
                  <Icon name="Code" size={16} className="mr-2 inline" />
                  Technical Skills
                </button>
                <button
                  onClick={() => setSkillsView('soft')}
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    skillsView === 'soft'
                      ? 'bg-primary text-primary-foreground shadow-lg'
                      : 'text-text-secondary hover:text-foreground'
                  }`}
                >
                  <Icon name="User" size={16} className="mr-2 inline" />
                  Soft Skills
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Technical Skills */}
        {skillsView === 'technical' && (
          <section className="py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Technical Expertise
                </h2>
                <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                  Specialized knowledge in cybersecurity tools, technologies, and methodologies
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {technicalSkills.map((category) => (
                  <div 
                    key={category.id} 
                    className={`group bg-surface rounded-xl p-6 security-shadow hover:security-shadow-strong transition-all duration-300 transform hover:-translate-y-1 border border-border/50 hover:border-primary/20 relative overflow-hidden ${
                      activeCategory === category.id ? 'ring-2 ring-primary' : ''
                    }`}
                    onMouseEnter={() => setActiveCategory(category.id)}
                    onMouseLeave={() => setActiveCategory(null)}
                  >
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                    
                    <div className="relative z-10">
                      {/* Icon and Title */}
                      <div className="flex items-center mb-6">
                        <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                          <Icon name={getSkillCategoryIcon(category.category)} size={24} className="text-primary group-hover:text-secondary transition-colors duration-300" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors duration-300">
                            {category.category}
                          </h3>
                          <p className="text-sm text-text-secondary">{category.skills.length} skills</p>
                        </div>
                      </div>
                      
                      {/* Skills List */}
                      <div className="space-y-3">
                        {category.skills.map((skill, skillIndex) => (
                          <div key={skillIndex} className="flex items-start group/skill">
                            <div className="w-2 h-2 bg-accent rounded-full mt-2 mr-3 flex-shrink-0 group-hover/skill:bg-primary transition-colors"></div>
                            <span className="text-text-secondary group-hover/skill:text-foreground transition-colors leading-relaxed">
                              {skill}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Soft Skills */}
        {skillsView === 'soft' && (
          <section className="py-20">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Professional Skills
                </h2>
                <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                  Essential interpersonal and professional skills that complement technical expertise
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                {softSkills.map((item) => (
                  <div key={item.id} className="group bg-surface rounded-xl p-8 security-shadow hover:security-shadow-strong transition-all duration-300 transform hover:-translate-y-1 border border-border/50 hover:border-secondary/20 relative overflow-hidden">
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                    
                    <div className="relative z-10">
                      <div className="flex items-start">
                        <div className="w-14 h-14 bg-gradient-to-br from-secondary/10 to-accent/10 rounded-xl flex items-center justify-center mr-6 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                          <Icon name="User" size={24} className="text-secondary group-hover:text-accent transition-colors duration-300" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-foreground mb-3 group-hover:text-secondary transition-colors duration-300">
                            {item.skill}
                          </h3>
                          <p className="text-text-secondary leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Skills Proficiency */}
        <section className="py-20 bg-gradient-to-br from-muted/30 to-muted/10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 bg-accent/10 rounded-full text-accent font-medium text-sm mb-6">
                <Icon name="TrendingUp" size={16} className="mr-2" />
                Skill Proficiency
              </div>
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Expertise Levels
              </h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                Proficiency levels based on practical experience and continuous learning
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Core Competencies */}
              <div className="bg-surface rounded-xl p-8 security-shadow">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                  <Icon name="Star" size={24} className="text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-6">Core Competencies</h3>
                <div className="space-y-4">
                  {[
                    { skill: 'SOC Operations', level: 95 },
                    { skill: 'Threat Detection', level: 90 },
                    { skill: 'Security Monitoring', level: 88 },
                    { skill: 'Incident Response', level: 85 }
                  ].map((item, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-foreground font-medium">{item.skill}</span>
                        <span className="text-primary font-bold">{item.level}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                        <div 
                          className="bg-gradient-to-r from-primary to-secondary h-full rounded-full transition-all duration-1000 ease-out"
                          style={{ width: `${item.level}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Specialized Skills */}
              <div className="bg-surface rounded-xl p-8 security-shadow">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-6">
                  <Icon name="Target" size={24} className="text-secondary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-6">Specialized Skills</h3>
                <div className="space-y-4">
                  {[
                    { skill: 'Malware Analysis', level: 82 },
                    { skill: 'Digital Forensics', level: 78 },
                    { skill: 'Security Automation', level: 85 },
                    { skill: 'Regulatory Compliance', level: 80 }
                  ].map((item, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-foreground font-medium">{item.skill}</span>
                        <span className="text-secondary font-bold">{item.level}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                        <div 
                          className="bg-gradient-to-r from-secondary to-accent h-full rounded-full transition-all duration-1000 ease-out"
                          style={{ width: `${item.level}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Skill Development */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Continuous Learning
              </h2>
              <p className="text-lg text-text-secondary">
                Committed to staying current with evolving cybersecurity landscape
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-surface rounded-xl p-6 security-shadow text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="Book" size={24} className="text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Research & Study</h3>
                <p className="text-text-secondary text-sm">
                  Continuous learning through research papers, security blogs, and industry publications
                </p>
              </div>
              
              <div className="bg-surface rounded-xl p-6 security-shadow text-center">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="Users" size={24} className="text-secondary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Community Engagement</h3>
                <p className="text-text-secondary text-sm">
                  Active participation in cybersecurity communities and knowledge sharing
                </p>
              </div>
              
              <div className="bg-surface rounded-xl p-6 security-shadow text-center">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="Award" size={24} className="text-accent" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Certifications</h3>
                <p className="text-text-secondary text-sm">
                  Pursuing advanced certifications to validate and expand expertise
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-20 bg-gradient-to-br from-primary to-secondary relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl lg:text-4xl font-bold text-white">
                  Let's Build Something Secure
                </h2>
                <p className="text-xl text-white/90 max-w-2xl mx-auto">
                  Ready to leverage my skills and expertise for your cybersecurity initiatives?
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => window.location.href = '/projects'}
                  className="bg-white text-primary px-8 py-3 rounded-lg font-medium hover:bg-white/90 transition-all duration-200 transform hover:-translate-y-1 shadow-lg hover:shadow-xl"
                >
                  <Icon name="FolderOpen" size={18} className="mr-2 inline" />
                  View My Projects
                </button>
                <button
                  onClick={() => window.location.href = '/contact'}
                  className="border-2 border-white/30 text-white px-8 py-3 rounded-lg font-medium hover:bg-white/10 transition-all duration-200"
                >
                  <Icon name="Mail" size={18} className="mr-2 inline" />
                  Get In Touch
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Skills; 